import React, {memo, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowForward from '@mui/icons-material/ArrowForward';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, breakpoints, colors, fontSize, fontWeight, overlay, shadows} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';
import {NARROW_LAYOUT_MAX, betweenWidth} from '@/styles/breakpoints';

interface AudienceOrbitItem {
    id: string;
    label: string;
    title: string;
    description: string[];
    supportItems: string[];
}

interface AudienceOrbitSectionProps {
    heading: string;
    subheading: string;
    ctaText: string;
    onCtaClick?: () => void;
    centerLabel: string;
    items: AudienceOrbitItem[];
}

const sectionSx = {bgcolor: colors.primary.deep} as const;

const headerRowSx = {
    display: 'flex',
    flexDirection: {xs: 'column', md: 'row'},
    justifyContent: 'space-between',
    alignItems: {xs: 'flex-start', md: 'center'},
    gap: 3,
    mb: {xs: 4, md: 5}
} as const;

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.inverse,
        mb: 1.5
    }
] as const;

const headerTextSx = {flex: 1} as const;

// Below 1500px, where the text sits beside the button (`md` up), it is held short of the
// CTA so the copy doesn't run right up to it. A share of the text column rather than a
// fixed width, so it holds across the band.
const subheadingSx = {
    color: colors.text.inverse,
    lineHeight: 1.7,
    [betweenWidth(breakpoints.md, NARROW_LAYOUT_MAX)]: {maxWidth: 'calc(85% - 10px)'}
} as const;

const gridItemSx = {display: 'flex'} as const;

// Phones skip the orbit and its chip tabs: every audience card is listed in turn, so each
// reads on its own instead of hiding behind a tab.
const orbitGridItemSx = {display: {xs: 'none', md: 'flex'}} as const;
const desktopDetailSx = {display: {xs: 'none', md: 'flex'}, width: '100%'} as const;
const mobileDetailListSx = {display: {xs: 'flex', md: 'none'}, flexDirection: 'column', gap: 2, width: '100%'} as const;

const orbitCardSx = {
    position: 'relative',
    width: '100%',
    bgcolor: overlay.whiteHint,
    border: `1px solid ${overlay.whiteLight}`,
    borderRadius: borderRadius.xl,
    minHeight: {sm: 420, md: 460},
    p: {xs: 2, sm: 0},
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
} as const;

// A phone cannot hold seven labels around a ring without them colliding with each
// other and with the centre, so below `sm` the ring is dropped and the same chips
// render as a wrapped row. They stay the section's selector either way.
const orbitStageSx = {
    display: {xs: 'none', sm: 'block'},
    position: 'relative',
    width: {sm: 320, md: 380},
    height: {sm: 320, md: 380}
} as const;

const chipRowSx = {
    display: {xs: 'flex', sm: 'none'},
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 1,
    width: '100%'
} as const;

const flatChipSx = (active: boolean) =>
    ({
        px: 1.5,
        py: 1,
        borderRadius: borderRadius.md,
        fontSize: fontSize[13],
        fontWeight: fontWeight.medium,
        cursor: 'pointer',
        border: 'none',
        bgcolor: active ? colors.accent.orange : colors.background.default,
        color: active ? colors.text.inverse : colors.text.primary,
        boxShadow: shadows.sm,
        transition: 'background-color 0.2s ease, color 0.2s ease'
    } as const);

// Rings are drawn as SVG so the dash length and the gap between dashes can be set
// explicitly — a CSS `dashed` border gives no control over either.
const ringsSvgSx = {position: 'absolute', inset: 0, width: '100%', height: '100%'} as const;

const RING_RADII = [36, 28, 20];

const outerRingSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '88%',
    height: '88%',
    borderRadius: borderRadius.circle,
    border: `2px solid ${colors.accent.orange}`
} as const;

// Zero-width line pinned at the stage centre, rotated about its top end so it radiates outward.
const spokeSx = (angle: number) =>
    ({
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: '44%',
        borderLeft: `1px solid ${overlay.whiteBorder}`,
        transformOrigin: '0 0',
        transform: `rotate(${angle}deg)`
    } as const);

const centerCircleSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {xs: 108, md: 130},
    height: {xs: 108, md: 130},
    borderRadius: borderRadius.circle,
    bgcolor: colors.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    p: 1.5,
    boxShadow: shadows.lg,
    zIndex: 2
} as const;

const centerLabelSx = {
    color: colors.primary.deep,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[13],
    lineHeight: 1.3
} as const;

const chipSx = (active: boolean) =>
    ({
        position: 'absolute',
        px: {xs: 1.25, md: 1.5},
        py: 0.75,
        borderRadius: borderRadius.md,
        fontSize: {xs: fontSize[12], md: fontSize[13]},
        fontWeight: fontWeight.medium,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        border: 'none',
        transform: 'translate(-50%, -50%)',
        bgcolor: active ? colors.accent.orange : colors.background.default,
        color: active ? colors.text.inverse : colors.text.primary,
        boxShadow: shadows.sm,
        zIndex: 3,
        transition: 'background-color 0.2s ease, color 0.2s ease'
    } as const);

const detailCardSx = {
    width: '100%',
    bgcolor: colors.background.default,
    borderRadius: borderRadius.xl,
    borderLeft: `4px solid ${colors.accent.orange}`,
    minHeight: {md: 460},
    p: {xs: 3, md: 4}
} as const;

const detailHeaderRowSx = {display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5} as const;

const detailBadgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: borderRadius.md,
    bgcolor: colors.background.tag,
    color: colors.primary.deep,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[14]
} as const;

const detailTitleSx = {
    color: colors.text.primary,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[24]
} as const;

const detailDescSx = {color: colors.text.primary, lineHeight: 1.7, mb: 3} as const;

const supportLabelSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: colors.accent.orange,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[13],
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    mb: 2,
    '&::before': {
        content: '""',
        display: 'inline-block',
        width: 18,
        height: 2,
        bgcolor: colors.accent.orange
    }
} as const;

const supportGridSx = {display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1.5} as const;

const supportItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    bgcolor: colors.background.subtle,
    borderRadius: borderRadius.md,
    px: 1.75,
    py: 1.25,
    fontSize: fontSize[14],
    color: colors.text.primary
} as const;

const supportDotSx = {
    width: 6,
    height: 6,
    minWidth: 6,
    mt: 0.9,
    borderRadius: borderRadius.circle,
    bgcolor: colors.accent.orange
} as const;

// 7-point compass layout (45° apart, skipping straight-down) matching the data order:
// Startups, Enterprises, SaaS Companies, Funded Startups, Founders, Product Teams, Digital Agencies.
const ORBIT_LAYOUT = [
    {angle: -90, radius: 44},
    {angle: -45, radius: 44},
    {angle: 0, radius: 44},
    {angle: 45, radius: 44},
    {angle: 135, radius: 44},
    {angle: 180, radius: 44},
    {angle: -135, radius: 44}
];

const getChipPosition = (angle: number, radius: number) => {
    const radians = (angle * Math.PI) / 180;
    const left = 50 + radius * Math.cos(radians);
    const top = 50 + radius * Math.sin(radians);
    return {left: `${left}%`, top: `${top}%`};
};

const AUTOPLAY_INTERVAL_MS = 4000;

const renderDetailCard = (item: AudienceOrbitItem, index: number) => (
    <Box sx={detailCardSx}>
        <Box sx={detailHeaderRowSx}>
            <Typography component="span" sx={detailBadgeSx}>
                {String(index + 1).padStart(2, '0')}
            </Typography>
            <Typography component="h3" className="font-headers" sx={detailTitleSx}>
                {item.title}
            </Typography>
        </Box>
        {item.description.map((paragraph, idx) => (
            <Typography
                key={paragraph}
                className="font-regular-text"
                sx={idx === item.description.length - 1 ? detailDescSx : {...detailDescSx, mb: 1.5}}
            >
                {parse(paragraph)}
            </Typography>
        ))}

        <Typography component="span" sx={supportLabelSx}>
            We Support
        </Typography>
        <Box sx={supportGridSx}>
            {item.supportItems.map((support) => (
                <Box key={support} sx={supportItemSx}>
                    <Box sx={supportDotSx} />
                    {support}
                </Box>
            ))}
        </Box>
    </Box>
);

const AudienceOrbitSection = ({heading, subheading, ctaText, onCtaClick, centerLabel, items}: AudienceOrbitSectionProps) => {
    const [activeId, setActiveId] = useState(items?.[0]?.id);
    const [paused, setPaused] = useState(false);
    const activeItem = items?.find((item) => item.id === activeId) ?? items?.[0];

    useEffect(() => {
        if (paused || !items?.length) return undefined;
        const timer = setInterval(() => {
            setActiveId((current) => {
                const nextIndex = (items.findIndex((item) => item.id === current) + 1) % items.length;
                return items[nextIndex].id;
            });
        }, AUTOPLAY_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [paused, items]);

    return (
        <Box sx={sectionSx}>
            <MUIContainer>
                <Box sx={headerRowSx}>
                    <Box sx={headerTextSx}>
                        <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                            {heading}
                        </Typography>
                        <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                            {subheading}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        disableRipple={true}
                        className="animated-button nft-button"
                        onClick={onCtaClick}
                        endIcon={<ArrowForward fontSize="small" />}
                    >
                        {ctaText}
                    </Button>
                </Box>

                <Grid container spacing={4} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                    <Grid item xs={12} md={5} sx={orbitGridItemSx}>
                        <Box sx={orbitCardSx}>
                            <Box sx={chipRowSx}>
                                {items?.map((item) => (
                                    <Box
                                        key={item.id}
                                        component="button"
                                        type="button"
                                        onClick={() => setActiveId(item.id)}
                                        sx={flatChipSx(item.id === activeItem?.id)}
                                    >
                                        {item.label}
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={orbitStageSx}>
                                <Box sx={outerRingSx} />
                                <Box component="svg" sx={ringsSvgSx} viewBox="0 0 100 100" aria-hidden="true">
                                    {RING_RADII.map((radius) => (
                                        <circle
                                            key={radius}
                                            cx="50"
                                            cy="50"
                                            r={radius}
                                            fill="none"
                                            stroke={overlay.whiteBorder}
                                            strokeWidth="0.35"
                                            strokeDasharray="1.4 3"
                                        />
                                    ))}
                                </Box>
                                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                                    <Box key={angle} sx={spokeSx(angle)} />
                                ))}

                                <Box sx={centerCircleSx}>
                                    <Typography sx={centerLabelSx}>{centerLabel}</Typography>
                                </Box>

                                {items?.map((item, idx) => {
                                    const layout = ORBIT_LAYOUT[idx % ORBIT_LAYOUT.length];
                                    const position = getChipPosition(layout.angle, layout.radius);
                                    return (
                                        <Box
                                            key={item.id}
                                            component="button"
                                            type="button"
                                            onMouseEnter={() => setActiveId(item.id)}
                                            onFocus={() => setActiveId(item.id)}
                                            onClick={() => setActiveId(item.id)}
                                            sx={{...chipSx(item.id === activeItem?.id), ...position}}
                                        >
                                            {item.label}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={7} sx={gridItemSx}>
                        {activeItem && <Box sx={desktopDetailSx}>{renderDetailCard(activeItem, items.indexOf(activeItem))}</Box>}
                        <Box sx={mobileDetailListSx}>
                            {items?.map((item, idx) => (
                                <React.Fragment key={item.id}>{renderDetailCard(item, idx)}</React.Fragment>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(AudienceOrbitSection);
